import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { BehaviorSubject, forkJoin, of, Subscription } from "rxjs";
import { findIndex, isEmpty } from "lodash";
import { KlDetailFilterComponent } from "@app/modules/shared/components/kl-detail-filter/kl-detail-filter.component";
import { PrimengLocal } from "@app/modules/shared/constants/primeng-local";
import { FilterItem } from "@app/modules/shared/model/detail-filter.interface";
import { SpeCodeListComponent } from "@dashboard/modules/application-params/specode/components/specode-list/specode-list.component";
import { CurrentAccountListComponent } from "@dashboard/modules/finance-management/current-account/components/current-account-list/current-account-list.component";
import { DocumentTypesEnum } from "@app/modules/shared/enum/document-types.enum";
import {
  ExplorerService,
  ModalConfig,
  TransferState,
} from "@app/modules/shared/services/explorer.service";
import { KlSweetAlertService } from "@app/modules/shared/services/message/kl-sweet-alert.service";
import { KlLoadingService } from "@app/modules/shared/services/kl-loading.service";
import { UtilService } from "@app/modules/shared/services/util.service";
import { KlAlertService } from "@app/modules/shared/services/message/alert.service";
import { ConvertIsoDateService } from "@app/modules/shared/services/convert-iso-date.service";
import { TranslateService } from "@app/modules/shared/services/translate/translate.service";
import { ApiService } from "@app/modules/shared/services/base/api.service";
import { DynamicComponent } from "@app/data/dashboard/interfaces/dynamic-component.interface";
import { catchError, map } from "rxjs/operators";
import { CustomerListComponent } from "@dashboard/modules/shipping-management/customer/components/customer-list/customer-list.component";

@Component({
  selector: "kl-virtual-order-receipt-detail-filter",
  templateUrl: "./virtual-order-receipt-detail-filter.component.html",
  styleUrls: ["./virtual-order-receipt-detail-filter.component.scss"],
})
export class VirtualOrderReceiptDetailFilterComponent
  implements OnInit, OnDestroy
{
  @Input() entity;
  forkJoinStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  @ViewChild(forwardRef(() => KlDetailFilterComponent), { static: true })
  klDetailFilterComponent: any;
  locale =
    PrimengLocal[
      sessionStorage.getItem("currentLanguage")
        ? sessionStorage.getItem("currentLanguage").toUpperCase()
        : "TR"
    ];
  tunnel: Subscription;
  dateFormatString: string;
  alertMessage: string;
  alertShow = false;
  entryName: string;
  currentFilter: any;
  saveForm = false;
  selectedFilterValues: Array<FilterItem> = [];
  filterList = [];
  currentAccountCodeExpressionValue: any;
  customerCodeExpressionValue: any;
  documentNumExpressionValue: any;
  externalDocumentNumExpressionValue: any;

  private _firstDocumentDate: Date;
  set firstDocumentDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._firstDocumentDate = new Date(
          this.dateConvert.convertDate(value) + " 00:00:00"
        );
      } else if (typeof value === "string") {
        this._firstDocumentDate = new Date(value);
      }
      const payload = {
        label: "first_date",
        key: "firstDocumentDate",
        value: this._firstDocumentDate,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "first_date" },
        payload
      );
    } else {
      this._firstDocumentDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "first_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get firstDocumentDate() {
    return this._firstDocumentDate;
  }

  private _lastDocumentDate: Date;
  set lastDocumentDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._lastDocumentDate = new Date(
          this.dateConvert.convertDate(value) + " 23:59:59"
        );
      } else if (typeof value === "string") {
        this._lastDocumentDate = new Date(value);
      }
      const payload = {
        label: "last_date",
        key: "lastDocumentDate",
        value: this._lastDocumentDate,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_date" },
        payload
      );
    } else {
      this._lastDocumentDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastDocumentDate() {
    return this._lastDocumentDate;
  }

  private _approvalStatusValue: any;
  set approvalStatusValue(value: any) {
    this._approvalStatusValue = value;
    if (value) {
      const payload = {
        label: "approval_status",
        key: "approvalStatusValue",
        value: value,
        accessor: "status",
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "approval_status" },
        payload
      );
    } else {
      this._approvalStatusValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "approval_status",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get approvalStatusValue() {
    return this._approvalStatusValue;
  }

  private _currentAccountCodeValue: any;
  set currentAccountCodeValue(value: any) {
    if (value) {
      this._currentAccountCodeValue = value;
      const payload = {
        label: "current_account_code",
        key: "currentAccountCodeValue",
        value: value,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "current_account_code" },
        payload
      );
    } else {
      const index = findIndex(this.selectedFilterValues, {
        label: "current_account_code",
      });
      this._currentAccountCodeValue = null;
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get currentAccountCodeValue() {
    return this._currentAccountCodeValue;
  }

  private _customerCodeValue: any;
  set customerCodeValue(value: any) {
    if (value) {
      this._customerCodeValue = value;
      const payload = {
        label: "customer_code",
        key: "customerCodeValue",
        value: value,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "customer_code" },
        payload
      );
    } else {
      this._customerCodeValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "customer_code",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get customerCodeValue() {
    return this._customerCodeValue;
  }

  private _documentNumValue: any;
  set documentNumValue(value: any) {
    if (value) {
      this._documentNumValue = value;
      const payload = {
        label: "document_num",
        key: "documentNumValue",
        value: value,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "document_num" },
        payload
      );
    } else {
      this._documentNumValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "document_num",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get documentNumValue() {
    return this._documentNumValue;
  }

  private _externalDocumentNumValue: any;
  set externalDocumentNumValue(value: any) {
    if (value) {
      this._externalDocumentNumValue = value;
      const payload = {
        label: "external_document_num",
        key: "externalDocumentNumValue",
        value: value,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "external_document_num" },
        payload
      );
    } else {
      this._externalDocumentNumValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "external_document_num",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get externalDocumentNumValue() {
    return this._externalDocumentNumValue;
  }

  private _userValue: any;
  set userValue(value: any) {
    if (value) {
      this._userValue = value;
      const payload = {
        label: "user",
        key: "userValue",
        value: value,
        accessor: "username",
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "user" },
        payload
      );
    } else {
      this._userValue = null;
      const index = findIndex(this.selectedFilterValues, { label: "user" });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get userValue() {
    return this._userValue;
  }

  private _lastChangedUserValue: any;
  set lastChangedUserValue(value: any) {
    if (value) {
      this._lastChangedUserValue = value;
      const payload = {
        label: "last_changed_user",
        key: "lastChangedUserValue",
        value: value,
        accessor: "username",
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_changed_user" },
        payload
      );
    } else {
      this._lastChangedUserValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_changed_user",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastChangedUserValue() {
    return this._lastChangedUserValue;
  }

  private _firstDate: Date;
  set firstDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._firstDate = new Date(
          this.dateConvert.convertDate(value) + " 00:00:00"
        );
      } else if (typeof value === "string") {
        this._firstDate = new Date(value);
      }
      const payload = {
        label: "first_record_date",
        key: "firstDate",
        value: this._firstDate,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "first_record_date" },
        payload
      );
    } else {
      this._firstDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "first_record_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get firstDate() {
    return this._firstDate;
  }

  private _lastDate: Date;
  set lastDate(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._lastDate = new Date(
          this.dateConvert.convertDate(value) + " 23:59:59"
        );
      } else if (typeof value === "string") {
        this._lastDate = new Date(value);
      }
      const payload = {
        label: "last_record_date",
        key: "lastDate",
        value: this._lastDate,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_record_date" },
        payload
      );
    } else {
      this._lastDate = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_record_date",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastDate() {
    return this._lastDate;
  }

  private _changedUserValue: any;
  set changedUserValue(value: any) {
    if (value) {
      this._changedUserValue = value;
      const payload = {
        label: "last_changed_user",
        key: "changedUserValue",
        value: value,
        accessor: "username",
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_changed_user" },
        payload
      );
    } else {
      this._changedUserValue = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_changed_user",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get changedUserValue() {
    return this._changedUserValue;
  }

  private _firstDateLastChanged: Date;
  set firstDateLastChanged(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._firstDateLastChanged = new Date(
          this.dateConvert.convertDate(value) + " 00:00:00"
        );
      } else if (typeof value === "string") {
        this._firstDateLastChanged = new Date(value);
      }
      const payload = {
        label: "first_date_changed",
        key: "firstDateLastChanged",
        value: this._firstDateLastChanged,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "first_date_changed" },
        payload
      );
    } else {
      this._firstDateLastChanged = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "first_date_changed",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get firstDateLastChanged() {
    return this._firstDateLastChanged;
  }

  private _lastDateLastChanged: Date;
  set lastDateLastChanged(value: any) {
    if (value) {
      if (value instanceof Date) {
        this._lastDateLastChanged = new Date(
          this.dateConvert.convertDate(value) + " 23:59:59"
        );
      } else if (typeof value === "string") {
        this._lastDateLastChanged = new Date(value);
      }
      const payload = {
        label: "last_date_changed",
        key: "lastDateLastChanged",
        value: this._lastDateLastChanged,
      };
      VirtualOrderReceiptDetailFilterComponent.insertOrUpdate(
        this.selectedFilterValues,
        { label: "last_date_changed" },
        payload
      );
    } else {
      this._lastDateLastChanged = null;
      const index = findIndex(this.selectedFilterValues, {
        label: "last_date_changed",
      });
      if (index >= 0) {
        this.selectedFilterValues.splice(index, 1);
      }
    }
  }

  get lastDateLastChanged() {
    return this._lastDateLastChanged;
  }

  userOptions: any;
  CustomerList = CustomerListComponent;
  CurrentAccountList = CurrentAccountListComponent;
  approvalStatusOptions = [
    {
      label: this.translateService.instant("ALL"),
      value: null,
    },
    {
      label: this.translateService.instant("DRAFT"),
      value: {
        status: "DRAFT",
      },
    },
    {
      label: this.translateService.instant("APPROVED"),
      value: {
        status: "APPROVED",
      },
    },
  ];
  currentAccountExpressionOptions = [
    {
      label: this.translateService.instant("LIKE"),
      value: "LIKE",
    },
    {
      label: this.translateService.instant("NOT_LIKE"),
      value: "NOT_LIKE",
    },
    {
      label: this.translateService.instant("EQ"),
      value: "EQ",
    },
    {
      label: this.translateService.instant("NOT_EQ"),
      value: "NOT_EQ",
    },
    // {
    //   label: this.translateService.instant('IN'),
    //   value: 'IN'
    // },
    // {
    //   label: this.translateService.instant('NOT_IN'),
    //   value: 'NOT_IN'
    // },
    {
      label: this.translateService.instant("START_WITH"),
      value: "START_WITH",
    },
    {
      label: this.translateService.instant("END_WITH"),
      value: "END_WITH",
    },
    {
      label: this.translateService.instant("GT"),
      value: "GT",
    },
    {
      label: this.translateService.instant("GE"),
      value: "GE",
    },
    {
      label: this.translateService.instant("LT"),
      value: "LT",
    },
    {
      label: this.translateService.instant("LE"),
      value: "LE",
    },
  ];

  static insertOrUpdate(array: any[], meta: any, value: any) {
    const index = findIndex(array, meta);
    if (index >= 0) {
      array.splice(index, 1);
      array.push(value);
      return;
    }
    return array.push(value);
  }

  constructor(
    private explorerService: ExplorerService,
    private alertService: KlSweetAlertService,
    private klLoadingService: KlLoadingService,
    private utilService: UtilService,
    private klAlertService: KlAlertService,
    private dateConvert: ConvertIsoDateService,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService,
    private apiService: ApiService
  ) {
    this.dateFormatString = UtilService.dateFormatString
      .toLowerCase()
      .replace("yy", "");
    this.tunnel = this.explorerService.transferState$.subscribe((state) => {
      switch (state.state) {
        case "VIRTUAL_ORDER_RECEIPT_DETAIL_FILTER_CURRENT_ACCOUNT_CODE_CURRENT_ITEM":
          this.currentAccountCodeValue = state.payload.code;
          this.cd.markForCheck();
          break;
        case "VIRTUAL_ORDER_RECEIPT_DETAIL_FILTER_CUSTOMER_CODE_CURRENT_ITEM":
          this.customerCodeValue = state.payload.code;
          this.cd.markForCheck();
          break;
      }
    });
  }

  ngOnInit() {
    this.currentAccountCodeExpressionValue = "LIKE";
    this.customerCodeExpressionValue = "LIKE";
    this.documentNumExpressionValue = "LIKE";
    this.externalDocumentNumExpressionValue = "LIKE";
    this.firstDocumentDate = new Date();
    this.lastDocumentDate = new Date();
    this.klLoadingService.show("loading");
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      module: this.entity.module,
      key: this.entity.state,
    };
    const preference = this.apiService
      .getFilteredData("design", commitObject)
      .pipe(
        map((response: any) => response),
        catchError((error) => this.handleError(error))
      );
    const users = this.apiService.getAllData("user").pipe(
      map((response: any) => response),
      catchError((error) => this.handleError(error))
    );
    forkJoin([preference, users])
      .finally(() => {
        this.klLoadingService.hide();
        if (this.entity.filter) {
          this.selectFilter({
            item: { content: JSON.stringify(this.entity.filter) },
            select: false,
          });
        }
        this.cd.markForCheck();
      })
      .subscribe(
        ([preferenceResponse, userResponse]) => {
          this.userOptions = userResponse;
          this.filterList = preferenceResponse.data;
          if (!this.entity.filter) {
            this.filterList.forEach((filter) => {
              if (filter.level_default === "DEFAULT") {
                this.selectFilter({ item: filter, select: true });
              }
            });
          }
          this.forkJoinStatus.next(true);
        },
        (error) => this.handleError(error)
      );
  }

  getFilters() {
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      module: this.entity.module,
      key: this.entity.state,
    };
    this.klLoadingService.show("loading");
    this.apiService.getFilteredData("design", commitObject).subscribe(
      (resPreference) => {
        this.filterList = resPreference.data;
        this.klLoadingService.hide();
        this.cd.markForCheck();
      },
      (errPreference) => this.handleError(errPreference)
    );
  }

  selectFilter(event) {
    this.clearAll();
    if (event.select) {
      this.currentFilter = event.item;
    }
    let selectObject: any;
    selectObject = JSON.parse(event.item.content);
    const filterObject = selectObject["filter"];
    const paramsObject = selectObject["parameters"];
    this.selectedFilterValues = [];
    const parametersObject = [];
    if (filterObject.hasOwnProperty("document_dates")) {
      if (filterObject["document_dates"]["min"]) {
        this.firstDocumentDate = this.dateConvert.utcDate(
          new Date(filterObject["document_dates"]["min"])
        );
      }
      if (filterObject["document_dates"]["max"]) {
        this.lastDocumentDate = this.dateConvert.utcDate(
          new Date(filterObject["document_dates"]["max"])
        );
      }
    }
    if (filterObject.hasOwnProperty("document_states")) {
      this.approvalStatusValue = {
        status: filterObject["document_states"][0],
      };
    }
    if (filterObject.hasOwnProperty("current_account")) {
      if (filterObject.current_account.hasOwnProperty("code")) {
        this.currentAccountCodeValue = filterObject["current_account"]["code"];
        paramsObject.forEach((params) => {
          if (params.name === "current_account_code") {
            this.currentAccountCodeExpressionValue =
              params.expression["operator"];
          }
        });
        parametersObject.push({
          name: "current_account_code",
          expression: {
            operator: this.currentAccountCodeExpressionValue,
          },
        });
      }
    }
    if (filterObject.hasOwnProperty("customer")) {
      if (filterObject.customer.hasOwnProperty("code")) {
        this.customerCodeValue = filterObject["customer"]["code"];
        paramsObject.forEach((params) => {
          if (params.name === "customer_code") {
            this.customerCodeExpressionValue = params.expression["operator"];
          }
        });
        parametersObject.push({
          name: "customer_code",
          expression: {
            operator: this.customerCodeExpressionValue,
          },
        });
      }
    }
    if (filterObject.hasOwnProperty("document_num")) {
      this.documentNumValue = filterObject["document_num"];
      paramsObject.forEach((params) => {
        if (params.name === "document_num") {
          this.documentNumExpressionValue = params.expression["operator"];
        }
      });
      parametersObject.push({
        name: "document_num",
        expression: {
          operator: this.documentNumExpressionValue,
        },
      });
    }
    if (filterObject.hasOwnProperty("external_document_num")) {
      this.externalDocumentNumValue = filterObject["external_document_num"];
      paramsObject.forEach((params) => {
        if (params.name === "external_document_num") {
          this.externalDocumentNumExpressionValue =
            params.expression["operator"];
        }
      });
      parametersObject.push({
        name: "external_document_num",
        expression: {
          operator: this.externalDocumentNumExpressionValue,
        },
      });
    }
    if (filterObject.hasOwnProperty("modified_user")) {
      this.lastChangedUserValue = this.userOptions.find(
        (user) => user.username === filterObject["modified_user"].username
      );
    }
    if (filterObject.hasOwnProperty("created_user")) {
      this.userValue = this.userOptions.find(
        (user) => user.username === filterObject["created_user"].username
      );
    }
    if (filterObject.hasOwnProperty("created_dates")) {
      if (filterObject["created_dates"]["min"]) {
        this.firstDate = this.dateConvert.utcDate(
          new Date(filterObject["created_dates"]["min"])
        );
      }
      if (filterObject["created_dates"]["max"]) {
        this.lastDate = this.dateConvert.utcDate(
          new Date(filterObject["created_dates"]["max"])
        );
      }
    }
    if (filterObject.hasOwnProperty("modified_dates")) {
      if (filterObject["modified_dates"]["min"]) {
        this.firstDateLastChanged = this.dateConvert.utcDate(
          new Date(filterObject["modified_dates"]["min"])
        );
      }
      if (filterObject["modified_dates"]["max"]) {
        this.lastDateLastChanged = this.dateConvert.utcDate(
          new Date(filterObject["modified_dates"]["max"])
        );
      }
    }
  }

  setModal(title: string, stateSelector: string, template: any, modalId) {
    const modalConfig = <ModalConfig>{
      title: title,
      stateSelector: "VIRTUAL_ORDER_RECEIPT_DETAIL_FILTER_" + stateSelector,
      childTemplate: <DynamicComponent>{
        mode: "DIALOG",
        modalId: modalId,
        dblSelect: true,
        template: template,
      },
    };
    this.explorerService.openModal(modalConfig);
  }

  deleteFromValues(event) {
    this.selectedFilterValues.splice(event.index, 1);
    this[event.item.key] = null;
  }

  deleteSavedFilter(event) {
    this.alertService.alertDelete(
      {
        title: "warning",
        text: "entry_must_be_delete",
        customClass: "kl-alert",
        showCloseButton: false,
      },
      () => {
        this.apiService.deleteData("design", event.item.id).subscribe(
          () => {
            this.clearAll();
            this.filterList.splice(event.index, 1);
            this.cd.markForCheck();
          },
          (errDeletePreference) => this.handleError(errDeletePreference)
        );
      }
    );
  }

  clearAll() {
    delete this.currentFilter;
    for (let i = 0; i < this.selectedFilterValues.length; i++) {
      this[this.selectedFilterValues[i].key] = null;
      i = i - 1;
    }
    this.selectedFilterValues = [];
  }

  commitSave(event: any) {
    this.saveForm = true;
    let commitObject: any;
    commitObject = {
      parent: {
        module: this.entity.parentModule,
      },
      level: event.levelStatus ? "APPLICATION" : "USER",
      level_default: event.levelDesign ? "DEFAULT" : "NO_DEFAULT",
      description: event.entryName,
      module: this.entity.module,
      type: "FILTER",
      key: this.entity.state,
      content: JSON.stringify(this.prepareFilterObject()),
    };
    switch (event.commitType) {
      case "add":
        this.apiService.addData("design", commitObject).subscribe(
          (resPreference) => {
            this.saveForm = false;
            this.getFilters();
            this.selectFilter({ item: resPreference, select: true });
            this.klDetailFilterComponent.closeEntryModal();
          },
          (errPreference) => {
            this.saveForm = false;
            this.handleError(errPreference);
          }
        );
        break;
      case "edit":
        commitObject["id"] = this.currentFilter.id;
        this.apiService.updateData("design", commitObject).subscribe(
          (resPreference) => {
            this.saveForm = false;
            this.getFilters();
            this.selectFilter({ item: resPreference, select: true });
            this.klDetailFilterComponent.closeEntryModal();
          },
          (errPreference) => {
            this.saveForm = false;
            this.handleError(errPreference);
          }
        );
        break;
    }
  }

  commitFilter() {
    if (
      this.firstDocumentDate > this.lastDocumentDate ||
      this.firstDate > this.lastDate ||
      this.firstDateLastChanged > this.lastDateLastChanged
    ) {
      this.alertMessage = this.translateService.instant(
        "end_date_cannot_be_less_than_start_date"
      );
      this.alertShow = true;
    } else {
      const preparedFilter: any = this.prepareFilterObject();
      if (preparedFilter.hasOwnProperty("close")) {
        delete preparedFilter.close;
      }
      if (isEmpty(preparedFilter["filter"]["modified_dates"])) {
        delete preparedFilter["filter"]["modified_dates"];
      }
      if (isEmpty(preparedFilter["filter"]["created_dates"])) {
        delete preparedFilter["filter"]["created_dates"];
      }
      if (isEmpty(preparedFilter["filter"]["document_dates"])) {
        delete preparedFilter["filter"]["document_dates"];
      }
      const state = <TransferState>{
        state: this.entity.state,
        payload: preparedFilter,
      };
      this.explorerService.transferState(state);
      this.explorerService.closeModal(this);
    }
  }

  closeFilter() {
    const preparedFilter = this.prepareFilterObject();
    Object.assign(preparedFilter, { close: true });
    this.explorerService.transferState({
      state: this.entity.state + "_DETAIL_FILTER",
      payload: preparedFilter,
    });
    this.explorerService.closeModal(this);
  }

  prepareFilterObject() {
    const filterObject = Object.create(null);
    filterObject["modified_dates"] = Object.create(null);
    filterObject["created_dates"] = Object.create(null);
    filterObject["document_dates"] = Object.create(null);
    const parametersObject = [];
    if (this.firstDocumentDate) {
      const date = this.dateConvert.convertDate(this.firstDocumentDate);
      const newDate = new Date(date + "T00:00:01.000Z");
      filterObject["document_dates"]["min"] = newDate.getTime();
      parametersObject.push({
        name: "document_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.lastDocumentDate) {
      const date = this.dateConvert.convertDate(this.lastDocumentDate);
      const newDate = new Date(date + "T23:59:59.000Z");
      filterObject["document_dates"]["max"] = newDate.getTime();
      parametersObject.push({
        name: "document_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.approvalStatusValue) {
      filterObject["document_states"] = [this.approvalStatusValue.status];
    }
    if (this.currentAccountCodeValue) {
      if (this.currentAccountCodeExpressionValue === "IN") {
        if (this.currentAccountCodeValue.includes(";")) {
          this.currentAccountCodeValue = this.currentAccountCodeValue.replace(
            " ",
            ""
          );
          filterObject["current_account"] = {
            code: this.currentAccountCodeValue.split(";"),
          };
        } else {
          filterObject["current_account"] = {
            code: [this.currentAccountCodeValue],
          };
        }
      } else {
        filterObject["current_account"] = {
          code: this.currentAccountCodeValue,
        };
      }
      parametersObject.push({
        name: "current_account_code",
        expression: { operator: this.currentAccountCodeExpressionValue },
      });
    }
    if (this.customerCodeValue) {
      if (this.customerCodeExpressionValue === "IN") {
        if (this.customerCodeValue.includes(";")) {
          this.customerCodeValue = this.customerCodeValue.replace(" ", "");
          filterObject["customer"] = {
            code: this.customerCodeValue.split(";"),
          };
        } else {
          filterObject["customer"] = {
            code: [this.customerCodeValue],
          };
        }
      } else {
        filterObject["customer"] = {
          code: this.customerCodeValue,
        };
      }
      parametersObject.push({
        name: "customer_code",
        expression: {
          operator: this.customerCodeExpressionValue,
        },
      });
    }
    if (this.documentNumValue) {
      filterObject["document_num"] = this.documentNumValue;
      parametersObject.push({
        name: "document_num",
        expression: {
          operator: this.documentNumExpressionValue,
        },
      });
    }
    if (this.externalDocumentNumValue) {
      filterObject["external_document_num"] = this.externalDocumentNumValue;
      parametersObject.push({
        name: "external_document_num",
        expression: {
          operator: this.externalDocumentNumExpressionValue,
        },
      });
    }
    if (this.userValue) {
      filterObject["created_user"] = { username: this.userValue.username };
    }
    if (this.lastChangedUserValue) {
      filterObject["modified_user"] = {
        username: this.lastChangedUserValue.username,
      };
    }
    if (this.firstDate) {
      const date = this.dateConvert.convertDate(this.firstDate);
      const newDate = new Date(date + "T00:00:01.000Z");
      filterObject["created_dates"]["min"] = newDate.getTime();
      parametersObject.push({
        name: "created_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.lastDate) {
      const date = this.dateConvert.convertDate(this.lastDate);
      const newDate = new Date(date + "T23:59:59.000Z");
      filterObject["created_dates"]["max"] = newDate.getTime();
      parametersObject.push({
        name: "created_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.firstDateLastChanged) {
      const date = this.dateConvert.convertDate(this.firstDateLastChanged);
      const newDate = new Date(date + "T00:00:01.000Z");
      filterObject["modified_dates"]["min"] = newDate.getTime();
      parametersObject.push({
        name: "modified_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    if (this.lastDateLastChanged) {
      const date = this.dateConvert.convertDate(this.lastDateLastChanged);
      const newDate = new Date(date + "T23:59:59.000Z");
      filterObject["modified_dates"]["max"] = newDate.getTime();
      parametersObject.push({
        name: "modified_dates",
        expression: { operator: "BETWEEN" },
      });
    }
    const query = {
      paging: {
        limit: this.entity.pageSize || 10,
        offset: 0,
        fetch: "EAGER",
      },
    };
    return Object.assign(
      {},
      { filter: filterObject },
      { parameters: parametersObject },
      { query: query }
    );
  }

  handleError(error) {
    this.alertMessage = this.utilService.alertErrorMessage(error);
    this.alertShow = true;
    this.klLoadingService.hide();
    return of();
  }

  ngOnDestroy() {
    this.tunnel.unsubscribe();
  }
}
